import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DeviceGateway, DeviceInstallation } from "@/contracts";
import { useTranslation } from "react-i18next";

import { installationsQuery, openInstallationMutation, unlinkGatewayMutation } from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { ErrorBlock, LoadingBlock } from "@/components/ui/page";
import { useApiClient } from "@/lib/api-context";
import { EM_DASH, formatDateTime, formatDevEui, formatRelative } from "@/lib/format";
import { FailInstallationDialog } from "./fail-installation-dialog";
import { GatewayStatusBadge, InstallationStatusBadge } from "./gateway-badges";
import { signalHint } from "./gateway-labels";
import { LinkGatewaysDialog } from "./link-gateways-dialog";

const OPEN_STATUSES = new Set(["DRAFT", "AWAITING_UPLINK"]);

/**
 * The installation of one device: which gateways carry it, and whether any of
 * them has actually been heard from.
 *
 * The distinction this screen exists to make visible is between a gateway
 * somebody configured and a gateway that has demonstrably carried a reading.
 * LoRaWAN has no attachment between a sensor and a gateway — the sensor
 * transmits and whatever is in range forwards it — so the configuration is a
 * statement of intent and only an arriving reading is evidence (ADR-062).
 */
export function InstallationCard({ deviceId }: { deviceId: string }) {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canInstall = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);

  const [linking, setLinking] = useState(false);
  const [failing, setFailing] = useState(false);

  const installations = useQuery(installationsQuery(client, deviceId));

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["devices", "installations", deviceId] });
    await queryClient.invalidateQueries({ queryKey: ["gateways"] });
  };

  const open = useMutation({ ...openInstallationMutation(client), onSuccess: invalidate });
  const unlink = useMutation({ ...unlinkGatewayMutation(client), onSuccess: invalidate });

  if (installations.isPending) {
    return <LoadingBlock label={t("installationLoading")} />;
  }

  if (installations.isError) {
    return <ErrorBlock error={installations.error} onRetry={() => void installations.refetch()} />;
  }

  const [current, ...history] = installations.data;
  const isOpen = current !== undefined && OPEN_STATUSES.has(current.status);

  return (
    <>
      <Card>
        <CardHeader
          title={t("installationTitle")}
          description={t("installationDescription")}
          action={
            canInstall ? (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setLinking(true)}>
                  {t("configureGatewaysTitle")}
                </Button>

                {isOpen ? (
                  <Button variant="danger" onClick={() => setFailing(true)}>
                    {t("abandon")}
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={open.isPending}
                    onClick={() => void open.mutateAsync({ deviceId, body: {} })}
                  >
                    {current ? t("reinstall") : t("startInstallation")}
                  </Button>
                )}
              </div>
            ) : null
          }
        />

        <CardBody className="flex flex-col gap-4">
          {open.isError ? <Alert>{open.error.message}</Alert> : null}
          {unlink.isError ? <Alert>{unlink.error.message}</Alert> : null}

          {current === undefined ? (
            <p className="text-sm text-ink-muted">
              This device has never been installed. Register the gateway that will carry it, then
              configure the two together.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <InstallationStatusBadge value={current.status} />

                <span className="text-sm text-ink-muted">
                  Started {formatDateTime(current.startedAt)}
                  {current.installedByName ? ` by ${current.installedByName}` : ""}
                </span>
              </div>

              {current.status === "AWAITING_UPLINK" ? (
                <Alert tone="info">
                  Nothing more to fill in. This device becomes operational when a reading arrives
                  through one of the gateways below — a sensor inside a metal-walled cold room
                  either reaches one or it does not, and only the reading can tell you which.
                </Alert>
              ) : null}

              {current.status === "FAILED" && current.failureReason ? (
                <Alert tone="warn" title={t("installationAbandonedWhy")}>
                  {current.failureReason}
                </Alert>
              ) : null}

              {current.notes ? <p className="text-sm text-ink-muted">{current.notes}</p> : null}

              <CoverageList
                coverage={current.gateways}
                canInstall={canInstall}
                pending={unlink.isPending}
                onRemove={(linkId) => void unlink.mutateAsync({ deviceId, linkId })}
              />
            </>
          )}

          {history.length > 0 ? <History entries={history} /> : null}
        </CardBody>
      </Card>

      <LinkGatewaysDialog
        deviceId={deviceId}
        open={linking}
        configured={current?.gateways ?? []}
        onClose={() => setLinking(false)}
      />

      <FailInstallationDialog
        deviceId={deviceId}
        open={failing}
        onClose={() => setFailing(false)}
      />
    </>
  );
}

function CoverageList({
  coverage,
  canInstall,
  pending,
  onRemove,
}: {
  coverage: DeviceGateway[];
  canInstall: boolean;
  pending: boolean;
  onRemove: (linkId: string) => void;
}) {
  const { t } = useTranslation("pages");

  if (coverage.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        No gateway configured yet. Until one is, there is nothing that could carry a reading.
      </p>
    );
  }

  const verified = coverage.filter((entry) => entry.verifiedAt !== null).length;

  return (
    <div className="flex flex-col gap-3">
      {coverage.length > 1 ? (
        <p className="text-sm text-ink-muted">
          {verified} of {coverage.length} configured gateways have carried a reading from this
          device.
          {verified < coverage.length
            ? " A second gateway is only redundancy once it has been heard through."
            : ""}
        </p>
      ) : null}

      <ul className="flex flex-col gap-3">
        {coverage.map((entry) => (
          <li
            key={entry.id}
            className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-line px-3 py-2"
          >
            <div className="flex flex-col gap-1">
              {entry.shared ? (
                <>
                  <span className="text-sm font-medium text-ink">
                    Covered by platform infrastructure
                  </span>
                  <span className="text-xs text-ink-muted">
                    A gateway we operate, shared with other sites nearby.
                  </span>
                </>
              ) : (
                <>
                  <span className="text-sm font-medium text-ink">{entry.name}</span>
                  <span className="font-mono text-xs text-ink-muted">
                    {formatDevEui(entry.eui)}
                    {entry.siteName ? ` · ${entry.siteName}` : ""}
                  </span>
                </>
              )}

              <Verification entry={entry} />
            </div>

            <div className="flex items-center gap-2">
              {entry.shared ? null : (
                <GatewayStatusBadge value={entry.status} />
              )}

              {canInstall ? (
                <Button
                  variant="secondary"
                  disabled={pending}
                  onClick={() => onRemove(entry.id)}
                  aria-label={
                    entry.shared
                      ? "Remove shared coverage"
                      : `Remove ${entry.name} from this device`
                  }
                >
                  {t("remove")}
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The evidence, or the absence of it.
 *
 * The signal is shown as a number with a word beside it rather than a word
 * alone: the dBm is the fact, and "workable" is a rule of thumb that has not
 * been measured for this hardware in these buildings.
 */
function Verification({ entry }: { entry: DeviceGateway }) {
  if (entry.verifiedAt === null) {
    return (
      <span className="text-xs text-ink-muted">
        No reading has arrived through it yet {EM_DASH} configured, not proven.
      </span>
    );
  }

  const hint = signalHint(entry.verifiedRssi);

  return (
    <span className="text-xs text-ink-muted">
      Carried a reading {formatRelative(entry.verifiedAt)}
      {entry.verifiedRssi === null ? null : (
        <>
          {" at "}
          <span className="font-medium text-ink">{entry.verifiedRssi} dBm</span>
          {hint ? ` (${hint.label})` : ""}
          {entry.verifiedSnr === null ? "" : `, SNR ${entry.verifiedSnr}`}
        </>
      )}
    </span>
  );
}

/** Previous attempts, kept because a device that was moved has a history. */
function History({ entries }: { entries: DeviceInstallation[] }) {
  return (
    <div className="border-t border-line pt-3">
      <h3 className="mb-2 text-sm font-medium text-ink">Earlier attempts</h3>
      <ul className="flex flex-col gap-2">
        {entries.map((entry) => (
          <li key={entry.id} className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
            <InstallationStatusBadge value={entry.status} />
            <span>
              {formatDateTime(entry.startedAt)}
              {entry.installedByName ? ` · ${entry.installedByName}` : ""}
              {entry.failureReason ? ` · ${entry.failureReason}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
